import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { pdfjsLib } from "../utils/pdfjs";

export interface Placement {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  type?: "signature" | "initial" | "date";
}

type PlaceTool = "signature" | "initial" | "date";

/** Size of the date stamp on screen. The server writes the real signing date
 *  into the same box, so the date always matches the signature record. */
const DATE_W = 132;
const DATE_H = 22;

/** Today's date as it will appear ("21 October 2026"). */
function todayText(): string {
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/London" }).format(new Date());
}

function DateStamp({ width, height, faint = false }: { width: number; height: number; faint?: boolean }) {
  return (
    <div
      className={`flex items-center whitespace-nowrap px-1 font-sans text-[13px] leading-none text-gray-900 ${faint ? "opacity-40" : ""}`}
      style={{ width, height }}
    >
      {todayText()}
    </div>
  );
}

export default function PdfSignaturePlacer({
  documentId,
  signatureDataUrl,
  initialsDataUrl,
  requiresInitials,
  onConfirm,
  onConfirmMulti,
  onCancel,
}: {
  documentId: number;
  signatureDataUrl: string;
  initialsDataUrl?: string;
  requiresInitials?: boolean;
  onConfirm: (placement: Placement) => void;
  onConfirmMulti?: (placements: Placement[]) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [sigNatural, setSigNatural] = useState({ w: 150, h: 60 });
  const [initNatural, setInitNatural] = useState({ w: 80, h: 40 });
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [activeTool, setActiveTool] = useState<PlaceTool>("signature");

  const SIG_DISPLAY_W = 150;
  const INIT_DISPLAY_W = 80;

  const hasSigPlacement = placements.some((p) => p.type === "signature");
  const hasInitPlacement = placements.some((p) => p.type === "initial");
  const hasDatePlacement = placements.some((p) => p.type === "date");
  const canConfirm = hasSigPlacement && (!requiresInitials || hasInitPlacement);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const ratio = img.height / img.width;
      setSigNatural({ w: SIG_DISPLAY_W, h: Math.round(SIG_DISPLAY_W * ratio) });
    };
    img.src = signatureDataUrl;
  }, [signatureDataUrl]);

  useEffect(() => {
    if (!initialsDataUrl) return;
    const img = new Image();
    img.onload = () => {
      const ratio = img.height / img.width;
      setInitNatural({ w: INIT_DISPLAY_W, h: Math.round(INIT_DISPLAY_W * ratio) });
    };
    img.src = initialsDataUrl;
  }, [initialsDataUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(
          `/api/documents/${documentId}/download?disposition=inline`,
          { credentials: "include" },
        );
        if (!resp.ok) throw new Error("Failed to fetch document");
        const ct = resp.headers.get("content-type") ?? "";
        let pdfData: ArrayBuffer;
        if (ct.includes("application/json")) {
          const data = (await resp.json()) as { url: string };
          const pdfResp = await fetch(data.url);
          pdfData = await pdfResp.arrayBuffer();
        } else {
          pdfData = await resp.arrayBuffer();
        }
        if (cancelled) return;
        const doc = await pdfjsLib.getDocument({ data: pdfData }).promise;
        if (cancelled) return;
        setPdfDoc(doc);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  const handleConfirm = () => {
    if (onConfirmMulti && (requiresInitials || placements.length > 1 || hasDatePlacement)) {
      onConfirmMulti(placements);
    } else if (placements.length > 0) {
      const sigPlacement = placements.find((p) => p.type === "signature");
      if (sigPlacement) onConfirm(sigPlacement);
    }
  };

  const removePlacement = (idx: number) => {
    setPlacements((prev) => prev.filter((_, i) => i !== idx));
  };

  const undoLast = () => {
    setPlacements((prev) => prev.slice(0, -1));
  };

  const clearAll = () => {
    setPlacements([]);
  };

  const activeDataUrl = activeTool === "initial" ? initialsDataUrl : signatureDataUrl;
  const activeW = activeTool === "initial" ? initNatural.w : activeTool === "date" ? DATE_W : sigNatural.w;
  const activeH = activeTool === "initial" ? initNatural.h : activeTool === "date" ? DATE_H : sigNatural.h;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("documents.placeSignature", "Place Your Signature")}
          </h2>
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b bg-gray-50 px-6 py-2">
          <button
            type="button"
            onClick={() => setActiveTool("signature")}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${activeTool === "signature" ? "bg-pme-blue text-white" : "bg-white text-gray-700 border border-gray-300"}`}
          >
            {t("documents.toolSignature", "Signature")}
            {hasSigPlacement && <span className="ml-1 text-[10px]">&#10003;</span>}
          </button>
          {requiresInitials && initialsDataUrl && (
            <button
              type="button"
              onClick={() => setActiveTool("initial")}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${activeTool === "initial" ? "bg-pme-blue text-white" : "bg-white text-gray-700 border border-gray-300"}`}
            >
              {t("documents.toolInitial", "Initial")}
              {hasInitPlacement && <span className="ml-1 text-[10px]">&#10003;</span>}
            </button>
          )}
          {onConfirmMulti && (
            <button
              type="button"
              onClick={() => setActiveTool("date")}
              title={t("documents.toolDateHint", "Add the date next to your signature. It's filled in with the day you sign.")}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${activeTool === "date" ? "bg-pme-blue text-white" : "bg-white text-gray-700 border border-gray-300"}`}
            >
              {t("documents.toolDate", "Date")}
              {hasDatePlacement && <span className="ml-1 text-[10px]">&#10003;</span>}
            </button>
          )}
          <div className="mx-2 h-5 w-px bg-gray-300" />
          <button
            type="button"
            onClick={undoLast}
            disabled={placements.length === 0}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 disabled:opacity-40"
          >
            {t("documents.undo", "Undo")}
          </button>
          <button
            type="button"
            onClick={clearAll}
            disabled={placements.length === 0}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 disabled:opacity-40"
          >
            {t("documents.clearAll", "Clear All")}
          </button>
        </div>

        <p className="border-b bg-blue-50 px-6 py-2 text-xs text-blue-700">
          {activeTool === "date"
            ? t("documents.placeDateHint", "Click where the date goes, for example on the Date line. It's filled in with the day you sign.")
            : t("documents.placeSignatureHint", "Click on the document where you want to place your signature. Drag to adjust position.")}
        </p>

        <div className="flex-1 overflow-y-auto bg-gray-200 p-4">
          {loading && (
            <div className="flex h-64 items-center justify-center">
              <p className="text-sm text-gray-500">{t("common.loading", "Loading...")}</p>
            </div>
          )}
          {error && (
            <div className="flex h-64 items-center justify-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          {pdfDoc && (
            <div className="space-y-4">
              {Array.from({ length: pdfDoc.numPages }, (_, i) => (
                <PdfPage
                  key={i}
                  doc={pdfDoc}
                  pageNum={i + 1}
                  activeDataUrl={activeDataUrl ?? signatureDataUrl}
                  activeWidth={activeW}
                  activeHeight={activeH}
                  activeTool={activeTool}
                  placements={placements}
                  onPlace={(p) => setPlacements((prev) => [...prev, p])}
                  onUpdatePlacement={(idx, p) =>
                    setPlacements((prev) => prev.map((old, i) => (i === idx ? p : old)))
                  }
                  onRemovePlacement={removePlacement}
                  signatureDataUrl={signatureDataUrl}
                  initialsDataUrl={initialsDataUrl}
                  sigWidth={sigNatural.w}
                  sigHeight={sigNatural.h}
                  initWidth={initNatural.w}
                  initHeight={initNatural.h}
                  draggingIdx={draggingIdx}
                  setDraggingIdx={setDraggingIdx}
                  dragOffset={dragOffset}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t px-6 py-4">
          <div className="text-sm text-gray-500">
            {placements.length > 0
              ? t("documents.itemsPlaced", "{{count}} item(s) placed", { count: placements.length })
              : t("documents.clickToPlace", "Click on the document to place signature")}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
            >
              {t("common.cancel", "Cancel")}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {t("documents.confirmPlacement", "Confirm Placement")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PdfPage({
  doc,
  pageNum,
  activeDataUrl,
  activeWidth,
  activeHeight,
  activeTool,
  placements,
  onPlace,
  onUpdatePlacement,
  onRemovePlacement,
  signatureDataUrl,
  initialsDataUrl,
  sigWidth,
  sigHeight,
  initWidth,
  initHeight,
  draggingIdx,
  setDraggingIdx,
  dragOffset,
}: {
  doc: pdfjsLib.PDFDocumentProxy;
  pageNum: number;
  activeDataUrl: string;
  activeWidth: number;
  activeHeight: number;
  activeTool: PlaceTool;
  placements: Placement[];
  onPlace: (p: Placement) => void;
  onUpdatePlacement: (idx: number, p: Placement) => void;
  onRemovePlacement: (idx: number) => void;
  signatureDataUrl: string;
  initialsDataUrl?: string;
  sigWidth: number;
  sigHeight: number;
  initWidth: number;
  initHeight: number;
  draggingIdx: number | null;
  setDraggingIdx: (idx: number | null) => void;
  dragOffset: React.MutableRefObject<{ x: number; y: number }>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0, scale: 1 });
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);

  const pagePlacements = placements
    .map((p, i) => ({ ...p, _idx: i }))
    .filter((p) => p.page === pageNum);
  const sizeOf = (type: PlaceTool | undefined) =>
    type === "initial" ? { w: initWidth, h: initHeight } : type === "date" ? { w: DATE_W, h: DATE_H } : { w: sigWidth, h: sigHeight };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const page = await doc.getPage(pageNum);
      if (cancelled) return;
      const containerWidth = wrapperRef.current?.parentElement?.clientWidth ?? 600;
      const maxWidth = Math.min(containerWidth - 32, 700);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = maxWidth / baseViewport.width;
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      await page.render({ canvasContext: ctx, viewport, canvas }).promise;
      setDims({ w: viewport.width, h: viewport.height, scale });
    })();
    return () => {
      cancelled = true;
    };
  }, [doc, pageNum]);

  const canvasToPlacement = useCallback(
    (cx: number, cy: number, w: number, h: number, type: PlaceTool): Placement => {
      const pdfX = cx / dims.scale;
      const pdfW = w / dims.scale;
      const pdfH = h / dims.scale;
      const pdfY = (dims.h - cy - h) / dims.scale;
      return { page: pageNum, x: pdfX, y: pdfY, width: pdfW, height: pdfH, type };
    },
    [dims, pageNum],
  );

  const handleClick = (e: React.MouseEvent) => {
    if (draggingIdx !== null) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const clampedX = Math.max(0, Math.min(cx, dims.w - activeWidth));
    const clampedY = Math.max(0, Math.min(cy, dims.h - activeHeight));
    onPlace(canvasToPlacement(clampedX, clampedY, activeWidth, activeHeight, activeTool));
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    if (draggingIdx !== null) {
      const dp = placements[draggingIdx];
      if (dp && dp.page === pageNum) {
        const { w: itemW, h: itemH } = sizeOf(dp.type);
        const cx = e.clientX - rect.left - dragOffset.current.x;
        const cy = e.clientY - rect.top - dragOffset.current.y;
        const clampedX = Math.max(0, Math.min(cx, dims.w - itemW));
        const clampedY = Math.max(0, Math.min(cy, dims.h - itemH));
        onUpdatePlacement(draggingIdx, canvasToPlacement(clampedX, clampedY, itemW, itemH, dp.type ?? "signature"));
      }
      return;
    }

    if (placements.length === 0 || true) {
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setCursorPos({ x: cx, y: cy });
    }
  };

  const handleMouseLeave = () => {
    setCursorPos(null);
  };

  return (
    <div
      ref={wrapperRef}
      className="relative mx-auto"
      style={{ width: dims.w || undefined }}
    >
      <div className="absolute right-2 top-2 z-10 rounded bg-black/50 px-2 py-0.5 text-xs text-white">
        {pageNum}
      </div>
      <canvas
        ref={canvasRef}
        className="block cursor-crosshair rounded shadow-lg"
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseUp={() => {
          if (draggingIdx !== null) setDraggingIdx(null);
        }}
      />

      {/* Cursor follower */}
      {cursorPos && draggingIdx === null && activeTool === "date" && (
        <div className="pointer-events-none absolute" style={{ left: cursorPos.x, top: cursorPos.y }}>
          <DateStamp width={activeWidth} height={activeHeight} faint />
        </div>
      )}
      {cursorPos && draggingIdx === null && activeTool !== "date" && (
        <img
          src={activeDataUrl}
          alt=""
          className="pointer-events-none absolute opacity-40"
          style={{
            left: cursorPos.x,
            top: cursorPos.y,
            width: activeWidth,
            height: activeHeight,
          }}
        />
      )}

      {/* Placed items */}
      {pagePlacements.map((p) => {
        const { w: itemW, h: itemH } = sizeOf(p.type);
        const screenX = p.x * dims.scale;
        const screenY = dims.h - (p.y + p.height) * dims.scale;
        const imgSrc = p.type === "initial" ? (initialsDataUrl ?? signatureDataUrl) : signatureDataUrl;

        return (
          <div
            key={p._idx}
            className="absolute"
            style={{ left: screenX, top: screenY, width: itemW, height: itemH }}
          >
            {p.type === "date" ? (
              <div
                className="absolute inset-0 cursor-grab border-2 border-blue-400 bg-white/60 active:cursor-grabbing"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setDraggingIdx(p._idx);
                  dragOffset.current = {
                    x: e.clientX - e.currentTarget.getBoundingClientRect().left,
                    y: e.clientY - e.currentTarget.getBoundingClientRect().top,
                  };
                }}
              >
                <DateStamp width={itemW - 4} height={itemH - 4} />
              </div>
            ) : (
            <img
              src={imgSrc}
              alt={p.type === "initial" ? "Initial" : "Signature"}
              className="absolute inset-0 cursor-grab border-2 border-blue-400 bg-white/30 active:cursor-grabbing"
              style={{ width: itemW, height: itemH }}
              draggable={false}
              onMouseDown={(e) => {
                e.stopPropagation();
                setDraggingIdx(p._idx);
                dragOffset.current = {
                  x: e.clientX - e.currentTarget.getBoundingClientRect().left,
                  y: e.clientY - e.currentTarget.getBoundingClientRect().top,
                };
              }}
            />
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemovePlacement(p._idx);
              }}
              className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white shadow hover:bg-red-600"
            >
              &times;
            </button>
            <span className="absolute -bottom-4 left-0 text-[9px] font-semibold text-blue-600">
              {p.type === "initial" ? "INI" : p.type === "date" ? "DATE" : "SIG"}
            </span>
          </div>
        );
      })}
    </div>
  );
}
